import {
  ActionList,
  ActionListItem,
  Box,
  Dropdown,
  DropdownOverlay,
  PlusIcon,
  SelectInput,
} from '@razorpay/blade/components';
import useSalaryComponentsList from 'components/RunPayroll/Popups/EditSalary/hooks/useSalaryComponentsList';
import { getSearchDropdownDescription } from 'components/RunPayroll/Popups/EditSalary/utils';
import AutoCompleteV2 from 'components/ui/AutoCompleteV2/AutoCompleteV2';
import { StandardSecondaryButton } from 'components/ui/Button';
import {
  ReviewSection,
  ReviewSectionHeader,
  ReviewSectionItem,
  ReviewSectionItemContainer,
} from 'components/WizardViews/components/review';
import { taxDeductionBehavior } from 'components/WizardViews/SalaryComponents/constants';
import {
  earningsExemptionDisplayText,
  isTaxExempt,
} from 'components/WizardViews/SalaryComponents/utils';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { dateFormats, getRunPayrollMonthList } from 'utils/Dates';
import { capitalizeAllWordsInString } from 'utils/Strings';
import { EarningsComponentType } from '../types';
import { routePaths } from 'components/Routes/data';

type ComponentWidgetV1Props = {
  name?: string;
};

const tdsDeductionTypes = (payrollMonth: string) => {
  const month = format(new Date(payrollMonth), 'MMMM yyyy');
  return {
    IMMEDIATE: { description: 'Deduct TDS from this payment itself', title: 'From this payment' },
    DURING_PAYROLL: {
      description: `Credit the entire amount, and deduct TDS from ${month} payroll`,
      title: `From ${month} payroll`,
    },
  };
};

const ComponentWidgetV1 = ({ name }: ComponentWidgetV1Props) => {
  const [selectedComponent, setSelectedComponent] = useState<EarningsComponentType | null>(null);
  const [payrollMonth, setPayrollMonth] = useState(format(new Date(), dateFormats.yearMonthDate));
  const [tdsDeductionType, setTdsDeductionType] = useState<string>();
  const taxBehaviour = selectedComponent?.settings.behaviour.taxBehaviour;
  const { searchSalaryComponents, isFetchingSalaryComponents } =
    useSalaryComponentsList<EarningsComponentType>({
      requestParams: {
        category: 'EARNINGS_CATEGORY',
        payType: 'LIST_ADHOC_TYPE',
        status: 'COMPONENT_STATUS_ACTIVE',
      },
    });

  const isTaxDeductionBehaviourInstant = selectedComponent
    ? selectedComponent.settings.behaviour.taxBehaviour?.taxDeductionBehaviour === 'instant'
    : false;

  const monthList = getRunPayrollMonthList(payrollMonth);

  const isExemptionVisible = isTaxExempt(
    selectedComponent?.settings.behaviour.taxBehaviour?.taxExemptInNewRegime,
    selectedComponent?.settings.behaviour.taxBehaviour?.taxExemptInOldRegime,
  );

  const redirectToComponentCreation = () => {
    // As widgets are used in php pages, this will work for react to php page redirection
    window.location.href = routePaths.salaryComponents.root
  };

  return (
    <Box display="flex" flexDirection="column" gap="spacing.6" paddingBottom="spacing.10">
      <Dropdown _width="100%">
        <SelectInput
          isRequired
          necessityIndicator="required"
          label="Select Payroll Month"
          name="pm"
          value={payrollMonth}
          onChange={({ name, values }) => {
            setPayrollMonth(values[0]);
          }}
        />
        <DropdownOverlay>
          <ActionList>
            {monthList.map((month) => (
              <ActionListItem
                key={month.toDateString()}
                value={format(month, 'yyyy-MM-dd')}
                title={format(month, 'MMMM yyyy')}
              />
            ))}
          </ActionList>
        </DropdownOverlay>
      </Dropdown>
      <Box>
        <input type="hidden" name={name} value={selectedComponent?.id} />
        <AutoCompleteV2
          necessityIndicator="required"
          isRequired
          label="Select component"
          placeholder="Select component"
          options={searchSalaryComponents()}
          isDisabled={isFetchingSalaryComponents}
          itemToKey={(item) => item.id}
          itemToLabel={(item) => capitalizeAllWordsInString(item.name)}
          itemToDescription={(item) => getSearchDropdownDescription(item) ?? ''}
          value={selectedComponent ? selectedComponent.id : undefined}
          onChange={(item) => setSelectedComponent(item)}
          onClearSelection={() => setSelectedComponent(null)}
          footer={
            <StandardSecondaryButton
              className="w-full h-14 flex items-center gap-3 justify-center"
              size="sm"
              onClick={redirectToComponentCreation}>
              <PlusIcon size="small" />
              Create addition
            </StandardSecondaryButton>
          }
        />

        {selectedComponent ? (
          <Box paddingTop="spacing.4">
            <ReviewSection>
              <ReviewSectionHeader title="Component" subtitle="details" />
              <ReviewSectionItemContainer>
                <ReviewSectionItem
                  label="TDS deduction"
                  value={
                    selectedComponent.settings.behaviour.taxBehaviour?.taxDeductionBehaviour
                      ? taxDeductionBehavior[
                          selectedComponent.settings.behaviour.taxBehaviour?.taxDeductionBehaviour
                        ].reviewLabel
                      : ''
                  }
                />
                <ReviewSectionItem
                  label="Tax exempted in Old Regime"
                  value={taxBehaviour?.taxExemptInOldRegime ? 'Yes' : 'No'}
                />
                <ReviewSectionItem
                  label="Tax exempted in New Regime"
                  value={taxBehaviour?.taxExemptInNewRegime ? 'Yes' : 'No'}
                />
                {isExemptionVisible && (
                  <ReviewSectionItem
                    label="Exemption under section"
                    value={earningsExemptionDisplayText(taxBehaviour?.taxExemptionSection)}
                  />
                )}
              </ReviewSectionItemContainer>
            </ReviewSection>
            <input
              type="hidden"
              name="display-name"
              value={selectedComponent.settings.behaviour.displayBehaviour?.displayName}
            />
          </Box>
        ) : null}
      </Box>

      {isTaxDeductionBehaviourInstant ? (
        <Dropdown>
          <SelectInput
            isRequired
            necessityIndicator="required"
            value={tdsDeductionType}
            label="Please choose how to recover TDS for this payment"
            placeholder="Select Option"
            name="tds-recovery-option"
            onChange={({ name, values }) => setTdsDeductionType(values[0])}
          />
          <DropdownOverlay>
            <ActionList>
              {Object.entries(tdsDeductionTypes(payrollMonth)).map(([key, item]) => (
                <ActionListItem
                  key={key}
                  title={item.title}
                  value={key}
                  description={item.description}
                />
              ))}
            </ActionList>
          </DropdownOverlay>
        </Dropdown>
      ) : null}
    </Box>
  );
};

export default ComponentWidgetV1;
